import { B2Button } from "@hdb/biome-2";
import * as moment from "moment";
import { FC, createRef, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import pdfIcon from "../../../assets/images/pdfBiomeIcon.svg";
import spreadsheetIcon from "../../../assets/images/spreadsheetIconBiome.svg";
import TableComponent from "../../../core/components/table/table.component";
import HasPermission from "../../../core/guards/has-permission";
import FilterFormComponent, {
  FilterFormConfig,
} from "../../../shared/filter-form/filter-form.component";
import {
  downloadFaultReportsFile,
  getAllFaultType,
  setFilter,
} from "../../../store/slices/fault-module/fault-report.slice";
import { getBlocksBySiteCode } from "../../../store/slices/location-block.slice";
import { getSiteByTCCode } from "../../../store/slices/location-site.slice";
import { getTownCouncils } from "../../../store/slices/town-council.slice";

interface FaultReportTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: (
    pageNumber: number,
    tcCode: any,
    siteCode: any,
    blockCode: any,
    reportType: any,
    startDate: any,
    endDate: any,
    isAcknowledged?: any
  ) => void;
  isLoading: boolean;
}

const FaultReportTableComponent: FC<FaultReportTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  isLoading,
}) => {
  const formRef = createRef();
  const [tcOptions, setTCOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [blockOptions, setBlockOptions] = useState<any>([]);
  const [faultTypeOptions, setFaultTypeOptions] = useState<any>([
    {
      label: "All Fault",
      value: 0,
    },
  ]);
  const [isFiltered, setIsFiltered] = useState<any>(false);
  const [downloaDocumentPayload, setDownloaDocumentPayload] = useState<any>({
    townCouncil: "",
    site: "",
    block: "",
    reportType: "",
    startDate: "",
    endDate: "",
    isAcknowledged: "3",
  });

  const [filterUpdateCount, setFilterUpdateCount] = useState(0);

  const acknowledgeStatusOptions = [
    { label: "Acknowledged", value: 1 },
    { label: "Reported", value: 2 },
    { label: "Resolved", value: 4 },
    { label: "All Fault", value: 3 },
  ];

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
  }, [dispatch]);

  const townCouncilData = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const isLoadingTownCouncils = useSelector((state: any) => {
    return state.townCouncil.isLoadingTownCouncils;
  });

  const siteDataByTCCode = useSelector((state: any) => {
    return state.locationSite.sitesByTCCode;
  });

  const isLocationSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.isLocationSitesByTCCode;
  });

  const blockDataSiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  const isLoadingBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.isLoadingBlocksBySiteCode;
  });

  const isLoadingFaultReportsDownload = useSelector((state: any) => {
    return state.faultReport.isLoadingFaultReportsDownload;
  });

  useEffect(() => {
    dispatch(getAllFaultType()).then((res: any) => {
      const types = res?.payload?.data?.map((type: any) => ({
        label: type?.faultName,
        value: type?.value,
      }));
      setFaultTypeOptions(types);
    });
  }, []);

  const [currentTC, setCurrentTC] = useState({ label: "", value: "" });

  useEffect(() => {
    const tcs = townCouncilData?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));

    // if user is TC Admin only 1 tc is available
    if (tcs?.length === 1) {
      setCurrentTC(tcs[0]);
    }
    setTCOptions(tcs);
    setFilterUpdateCount(filterUpdateCount + 1);
  }, [townCouncilData]);

  useEffect(() => {
    if (currentTC?.value?.length) {
      setFormFilterInitialValues((prev) => ({
        ...prev,
        townCouncil: currentTC?.value,
      }));

      dispatch(getSiteByTCCode({ tcCode: currentTC?.value }));
    }
  }, [currentTC]);

  useEffect(() => {
    const sites = siteDataByTCCode?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    setSiteOptions(sites);
  }, [siteDataByTCCode]);

  useEffect(() => {
    const blocks = blockDataSiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    setBlockOptions(blocks);
  }, [blockDataSiteCode]);

  useEffect(() => {
    setSiteOptions([]);
    setBlockOptions([]);
  }, []);

  const onChangeTC = (selectedTC: any) => {
    setSiteOptions([]);
    setBlockOptions([]);

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      townCouncil: selectedTC,
      site: "",
      block: "",
    }));

    if (!selectedTC) return;
    dispatch(getSiteByTCCode({ tcCode: selectedTC }));
  };

  const onChangeSite = (selectedSite: any) => {
    setBlockOptions([]);

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      site: selectedSite,
      block: "",
    }));

    if (!selectedSite) return;
    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));
  };
  const onChangeBlock = (selectedBlock: any) => {
    // if (!selectedBlock) return;

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      block: selectedBlock,
    }));
  };

  const onChangeStartDate = (startDate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      startDate: startDate,
    }));
  };

  const onChangeEndDate = (endDate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      endDate: endDate,
    }));
  };
  const onChangeReportType = (reportType: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      reportType: reportType,
    }));
  };

  const onChangeAcknowledge = (acknowledge: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      isAcknowledged: acknowledge,
    }));
  };

  const onSubmitFilter = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();

      let {
        townCouncil,
        site,
        block,
        reportType,
        startDate,
        endDate,
        isAcknowledged,
      } = form.values;

      if (endDate) {
        if (endDate < startDate) {
          setIsFiltered(false);
          return;
        }
      }

      if (
        !townCouncil ||
        !reportType ||
        !startDate
        // !endDate
      ) {
        setIsFiltered(false);
        return;
      }

      if (reportType && reportType === "all") {
        reportType = null;
      }

      dispatch(setFilter(form.values));

      onRefetchData(
        1,
        townCouncil ? townCouncil : null,
        site ? site : null,
        block ? block : null,
        reportType && reportType !== "all" ? reportType : null,
        startDate ? moment.utc(startDate) : null,
        endDate ? moment.utc(endDate) : null,
        isAcknowledged ? isAcknowledged : 3
      );

      setDownloaDocumentPayload({
        townCouncil,
        site,
        block,
        reportType,
        startDate,
        endDate,
        isAcknowledged,
      });

      setIsFiltered(true);
    }
  };

  const onClickDownload = (fileType: any) => {
    const form: any = formRef.current;

    if (form) {
      // form.handleSubmit();
      let {
        townCouncil,
        site,
        block,
        reportType,
        startDate,
        endDate,
        isAcknowledged,
      } = downloaDocumentPayload;

      if (
        !townCouncil ||
        !reportType ||
        !startDate
        // !endDate
      ) {
        return;
      }

      if (reportType) {
        if (reportType === "all") {
          reportType = null;
        }
      }

      dispatch(
        downloadFaultReportsFile({
          fileType,
          tcCode: townCouncil ? townCouncil : null,
          siteCode: site ? site : null,
          blockCode: block ? block : null,
          reportType: reportType ? reportType : null,
          startDate: startDate ? moment.utc(startDate) : null,
          endDate: endDate ? moment.utc(endDate) : null,
          isAcknowledged,
        })
      );
    }
  };

  const [formFilterInitialValues, setFormFilterInitialValues] = useState({
    townCouncil: "",
    site: "",
    block: "",
    reportType: "",
    startDate: "",
    endDate: "",
    isAcknowledged: "3",
  });

  const onReset = () => {
    const form: any = formRef.current;

    setFilterUpdateCount(filterUpdateCount + 1);

    form.resetForm();
    setSiteOptions([]);
    setBlockOptions([]);
    setIsFiltered(false);
    dispatch(setFilter({}));
    setFormFilterInitialValues({
      townCouncil: "",
      site: "",
      block: "",
      reportType: "",
      startDate: "",
      endDate: "",
      isAcknowledged: "3",
    });
  };

  const filterActionConfig: any = {
    label: "Search",
    event: onSubmitFilter,
    variant: "primary",
  };

  const reset: any = {
    label: "Reset",
    event: onReset,
    variant: "secondary",
  };

  const emptyConfig = {
    title: "No Fault Found",
    description: "",
  };

  const currentDateTime = moment().format("YYYY-MM-DD");
  const date13MonthsAgo = moment().subtract(13, "months").format("YYYY-MM-DD");

  const filterFormConfig: FilterFormConfig = {
    controls: [
      {
        controlGroup: [
          {
            name: "townCouncil",
            placeholder: "Select Town Council *",
            type: "searchable-select",
            options: tcOptions,
            onChange: onChangeTC,
            required: true,
            isLoading: isLoadingTownCouncils,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "site",
            placeholder: "Select Site",
            type: "searchable-select",
            options: siteOptions,
            onChange: onChangeSite,
            required: true,
            isLoading: isLocationSitesByTCCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "block",
            placeholder: "Select Block",
            type: "searchable-select",
            options: blockOptions,
            onChange: onChangeBlock,
            required: true,
            isLoading: isLoadingBlocksBySiteCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "reportType",
            placeholder: "Select Report Type *",
            type: "select",
            onChange: onChangeReportType,
            options: faultTypeOptions,
            required: true,
            isLoading: false,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "startDate",
            placeholder: "Select Start Date *",
            type: "date",
            onChange: onChangeStartDate,
            required: true,
            isLoading: false,
            minDate: date13MonthsAgo,
            maxDate: currentDateTime,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "endDate",
            placeholder: "Select End Date",
            type: "date",
            onChange: onChangeEndDate,
            required: true,
            isLoading: false,
            minDate: date13MonthsAgo,
            maxDate: currentDateTime,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "isAcknowledged",
            placeholder: "Select Status",
            type: "select",
            onChange: onChangeAcknowledge,
            options: acknowledgeStatusOptions,
            required: false,
            isLoading: false,
            hideSelectPlaceholder: true,
          },
        ],
      },
    ],
  };

  const navigate = useNavigate();

  const redirectToConfiguration = () => {
    navigate("/admin/configuration/reportconfiguration");
  };

  const validationSchema = yup.object().shape({
    townCouncil: yup.string().required("Town Council is required"),
    /*     site: yup.string().required("Site Council is required"),
    block: yup.string().required("Block Council is required"), */
    reportType: yup.string().required("Report Type is required"),
    startDate: yup.date().required("Start Date is required"),
    endDate: yup
      .date()
      .min(yup.ref("startDate"), "End Date must be after Start Date"),
    // .required("End Date is required"),
  });

  return (
    <div>
      {/*       <div className="flex justify-end table-generic-action mb-2">
        <B2Button
          className="icon-text-btn"
          variant="primary"
          onClick={redirectToConfiguration}
        >
          Automatic Report Scheduler
          <FontAwesomeIcon icon={faCalendar} className="fs-7 ms-2" />
        </B2Button>
      </div> */}

      <div>
        <FilterFormComponent
          key={formFilterInitialValues?.townCouncil}
          formRef={formRef}
          initialValues={formFilterInitialValues}
          formConfig={filterFormConfig}
          actionConfig={filterActionConfig}
          resetButton={reset}
          validation={validationSchema}
          filterUpdateCount={filterUpdateCount}
        />
      </div>
      <div>
        {isFiltered && (
          <div className="my-3">
            <div className="export py-4 d-flex align-items-center justify-content-end">
              <HasPermission
                moduleName="Fault Report"
                actionName="Download-Fault Report"
                isRoute={false}
              >
                <div className="d-flex gap-2">
                  <B2Button
                    variant="light"
                    onClick={() => onClickDownload("downloadPdf")}
                    disabled={
                      !data || (Array.isArray(data) && data.length === 0)
                    }
                  >
                    <img className="fs-7 me-2" src={pdfIcon} alt="PDF Icon" />
                    {" "}Export as PDF
                  </B2Button>

                  <B2Button
                    variant="light"
                    onClick={() => onClickDownload("downloadExcel")}
                    disabled={
                      !data || (Array.isArray(data) && data.length === 0)
                    }
                  >
                    <img
                      className="fs-7 me-2"
                      src={spreadsheetIcon}
                      alt="Spreadsheet Icon"
                    />{" "}
                    Export as Excel
                  </B2Button>
                </div>

                {isLoadingFaultReportsDownload ? (
                  <Spinner
                    className="ms-2"
                    style={{ color: "#1976D2" }}
                    animation="border"
                    role="status"
                    size="sm"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                ) : null}
              </HasPermission>
            </div>
            <TableComponent
              tableHeaders={columns}
              data={data}
              totalNumberOfRecords={totalNumberOfRecords}
              pageSize={pageSize}
              current={current}
              onChangePage={onChangePage}
              onChangeSort={onChangeSort}
              emptyConfig={emptyConfig}
              isLoadingData={isLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FaultReportTableComponent;
